import { Injectable } from '@angular/core';
import { DailyParticipant } from '@daily-co/daily-js';
import { Participants, Participant } from '../../models/participant.model';
import { ApiService } from '../api/api.service';

import DailyIframe from '@daily-co/daily-js';
import { BehaviorSubject, Subject } from 'rxjs';
import { ScreenShareService } from '../screen-share/screen-share.service';
import { RoomService } from '../room/room.service';
import { UserService } from '../user/user.service';
import LogRocket from 'logrocket';

/**
 * Sets the video, audio and screen track states in [formatParticipantObj]{@link ParticipantService#formatParticipantObj}
 */
const PLAYABLE_STATE = 'playable';

/**
 * Sets the video, audio and screen track states in [formatParticipantObj]{@link ParticipantService#formatParticipantObj}
 */
const LOADING_STATE = 'loading';

@Injectable({
    providedIn: 'root',
})
export class ParticipantService {
    /**
     * An array of participants
     *  - Used to find the [localParticipant]{@link ParticipantService#localParticipant}
     *  - Used to find the [participant sharing their screen]{@link ParticipantService#screenShareParticipants}
     *  - Used to find the [moderators]{@link ParticipantService#moderators}
     *  - Used to find the [participantInViewMax]{@link ParticipantService#participantsInView} for the observer
     *  - Used to calculate the [participantPaginationCount]{@link ParticipantService#participantPaginationCount}
     *  - Used to find the [participantsInView]{@link ParticipantService#participantsInView}
     * - Used to find the [participantsNotInView]{@link ParticipantService#participantsNotInView}
     *  - Participants are added to this array in [addParticipant]{@link ParticipantService#addParticipant}, and set their role. This adds a new participant to the call.
     *  - Used to find the existing participant in [updateTrack]{@link ParticipantService#updateTrack} and update their audio/video/screen tracks.
     *  - Used to update the current speaker in [updateCurrentSpeaker]{@link ParticipantService#updateCurrentSpeaker}
     * - Used to update the local participant in [updateLocalParticipant]{@link ParticipantService#updateLocalParticipant}
     *  - Used to remove the participant's preview video in [removePreview]{@link ParticipantService#removePreview}
     *  - Used in [participantJoined]{@link StreamProviderService#participantJoined} to add a new participant to the call.
     *  - Used in [handleParticipantLeft]{@link StreamProviderService#handleParticipantLeft} to remove a participant from the call.
     *  - Used in [handleAppMessage]{@link StreamProviderService#handleAppMessage} to add a chat message
     *  - Used to set the [participants]{@link RoomComponent#participants} getter in the RoomComponent
     *  - Used to set [participants]{@link RoomDetailsComponent#participants} getter in the RoomDetailsComponent
     *  - Used to set [observers]{@link RoomDetailsComponent#observers} getter in the RoomDetailsComponent
     * - Used to set [moderators]{@link RoomDetailsComponent#moderators} getter in the RoomDetailsComponent
     *  - Used to set [participants]{@link RoomDetailsComponent#participants} getter in the RoomDetailsComponent
     *  - Used to set [observers]{@link RoomDetailsComponent#observers} getter in the RoomDetailsComponent
     *  - Used to set [moderators]{@link RoomDetailsComponent#moderators} getter in the RoomDetailsComponent
     */
    participants: Participant[] = [];

    /**
     * A subject that emits when the participants are updated
     *  - Updated with an empty string in [addParticipant]{@link ParticipantService#addParticipant}
     *  - Subscribed to in [setupEvents]{@link RoomComponent#setupEvents} in the RoomComponent
     * - Subscribed to in [setupEvents]{@link RoomControlsComponent#setupEvents} in the RoomControlsComponent
     * - Subscribed to in [setupEvents]{@link RoomDetailsComponent#setupEvents} in the RoomDetailsComponent
     * - Subscribed to in [setupEvents]{@link SetupComponent#setupEvents} in the SetupComponent
     */
    participantsUpdated = new Subject();

    /**
     * A subject that emits when the tracks are updated
     * - Updated with an empty string in [toggleCamera]{@link ParticipantService#toggleCamera}
     * - Updated with an empty string in [togglemic]{@link ParticipantService#togglemic}
     *  - Updated with an empty string in [updateTrack]{@link ParticipantService#updateTrack}
     *  - Subscribed to in [ngOnInit]{@link ParticipantComponent#ngOnInit} in the ParticipantComponent
     *  - Subscribed to in [setupEvents]{@link RoomControlComponent#setupEvents} in the RoomControlComponent
     *  - Subscribed to in [ngOnInit]{@link ParticipantDetailsComponent#ngOnInit} in the ParticipantDetailsComponent
     */
    tracksUpdated = new Subject();

    /**
     * A variable to show the current speaker
     *  - Unused
     */
    currentSpeaker: any;

    /**
     * A subject that emits when the current speaker is updated
     * - Updated with an empty string in [updateCurrentSpeaker]{@link ParticipantService#updateCurrentSpeaker}
     * - Subscribed to in [setupEvents]{@link RoomComponent#setupEvents} in the RoomComponent
     */
    currentSpeakerUpdated = new Subject();

    /**
     * A subject that emits when the devices are updated
     * - Updated with an empty string in [setDevices]{@link ParticipantService#setDevices}
     * - Subscribed to in [setupEvents]{@link SetupComponent#setupEvents} in the SetupComponent
     */
    devicesUpdated = new Subject();

    /**
     * A subject that emits when the active devices are updated
     * - Updated with an empty string in [setDevices]{@link ParticipantService#setDevices}
     * - Subscribed to in [setupEvents]{@link SetupComponent#setupEvents} in the SetupComponent
     */
    activeDevicesUpdated = new Subject();

    /**
     * A variable listing the active devices
     *  - Used to set the [activeMic]{@link ParticipantService#activeMic} getter
     * - Used to set the [activeCamera]{@link ParticipantService#activeCamera} getter
     * - Used to set the [activeSpeaker]{@link ParticipantService#activeSpeaker} getter
     *  - Set in [setDevices]{@link ParticipantService#setDevices}
     */
    activeDevices: any;

    /**
     * A variable listing the available input devices
     * - Used to set the [availableCameras]{@link ParticipantService#availableCameras} getter
     * - Used to set the [availableMics]{@link ParticipantService#availableMics} getter
     * - Used to set the [availableSpeakers]{@link ParticipantService#availableSpeakers} getter
     * - Set in [setDevices]{@link ParticipantService#setDevices}
     */
    availableInputDevices: any;

    /**
     * A subject that emits when the audio level is updated
     *  - Set in [handleAudioLevel]{@link StreamProviderService#handleAudioLevel} in the StreamProviderService
     *  - Set in [setupAudioLevels]{@link SetupComponent#setupAudioLevels} in the SetupComponent
     *  - Used to set the [audioLevel]{@link ParticipantService#audioLevel} getter
     *  - Subscribed to in [setupEvents]{@link SetupComponent#setupEvents} in the SetupComponent
     */
    audioLevelUpdated = new BehaviorSubject<any>(0);

    /**
     * The active page of participants
     *  - Set in [updatePagination]{@link RoomComponent#updatePagination} in the RoomComponent
     * - Used to set the [participantsOffset]{@link ParticipantService#participantsOffset} getter
     *  - Used to set the [participantsActivePage]{@link RoomComponent#participantsActivePage} getter in the RoomComponent
     */
    participantsActivePage = 0;

    ownHandRaised: BehaviorSubject<any> = new BehaviorSubject(false);

    public ccEnabled = new BehaviorSubject(false);

    participantNetworkStatus: BehaviorSubject<any> = new BehaviorSubject('');

    /**
     * Gets the call instance from the DailyIframe
     *  - Sets the [waitingRoomParticipants]{@link ParticipantService#waitingRoomParticipants} getter
     *  - Sets the [noiseCancellationEnabled]{@link ParticipantService#noiseCancellationEnabled} getter
     *  - Used in [setDevices]{@link ParticipantService#setDevices}
     *  - Used in [changeMediaDevice]{@link ParticipantService#changeMediaDevice}
     *  - Used in [toggleCamera]{@link ParticipantService#toggleCamera}
     *  - Used in [togglemic]{@link ParticipantService#togglemic}
     *  - Used in [grantRoomAccess]{@link ParticipantService#grantRoomAccess}
     * - Used in [denyRoomAccess]{@link ParticipantService#denyRoomAccess}
     *  - Used in [leaveSession]{@link ParticipantService#leaveSession}
     *  - Used in [updateLocalParticipant]{@link ParticipantService#updateLocalParticipant}
     * - Used in [toggleNoiseCancellation]{@link ParticipantService#toggleNoiseCancellation}
     *  - Used in [toggleMic]{@link ParticipantDetailsComponent#toggleMic} in the ParticipantDetailsComponent
     * - Used in [toggleCamera]{@link ParticipantDetailsComponent#toggleCamera} in the ParticipantDetailsComponent
     * - Used in [removeFromCall]{@link ParticipantDetailsComponent#removeFromCall} in the ParticipantDetailsComponent
     *  - Sets the [networkQuality]{@link SetupComponent#networkQuality} getter in the SetupComponent
     *  - Used in [ngOnDestroy]{@link SetupComponent#ngOnDestroy} in the SetupComponent
     *  - Used in [setupAudioLevels]{@link SetupComponent#setupAudioLevels} in the SetupComponent
     */
    public get callObject(): any {
        return DailyIframe.getCallInstance();
    }

    /**
     * Gets the local participant from the list of [participants]{@link ParticipantService#participants}
     *  - Sets [audioLevel]{@link ParticipantService#audioLevel} getter
     *  - Sets [cameraEnabled]{@link ParticipantService#cameraEnabled} getter
     *  Sets [shareScreenEnabled]{@link RoomControlsComponent#shareScreenEnabled} getter in the RoomControlsComponent
     *  - Sets [isSharingScreen]{@link RoomControlsComponent#isSharingScreen} getter in the RoomControlsComponent
     *  - Sets [localParticipant]{@link SetupComponent#localParticipant} getter in the SetupComponent
     */
    public get localParticipant() {
        return this.participants.find((p: any) => p.local);
    }

    /**
     * Gets the list of participants in the waiting room, from the [callObject]{@link ParticipantService#callObject}
     *  - Sets the [grantAllAccess]{@link ParticipantService#grantAllAccess} getter
     *  - Sets the [waitingRoomParticipants]{@link RoomControlsComponent#waitingRoomParticipants} getter in the RoomControlsComponent
     *  - Sets the [waitingRoomParticipants]{@link RoomDetailsComponent#waitingRoomParticipants} getter in the RoomDetailsComponent
     */
    public get waitingRoomParticipants(): Participant[] {
        // ?? {} : callObject can be null during teardown, and Object.values(undefined)
        // throws. This getter is read from templates every change-detection cycle.
        return Object.values(this.callObject?.waitingParticipants() ?? {});
    }

    /**
     * Gets the list of participants sharing their screen, from the [participants]{@link ParticipantService#participants} getter
     *  - Sets the [screenShareParticipants]{@link RoomComponent#screenShareParticipants} getter in the RoomComponent
     * - Sets the [shareScreenEnabled]{@link RoomControlsComponent#shareScreenEnabled} getter in the RoomControlsComponent
     *  - Sets the [isSharingScreen]{@link RoomControlsComponent#isSharingScreen} getter in the RoomControlsComponent
     *  - Used in [handleParticipantLeft]{@link StreamProviderService#handleParticipantLeft} in the StreamProviderService
     *  - Used in [handleParticipantUpdated]{@link StreamProviderService#handleParticipantUpdated} in the StreamProviderService
     */
    get screenShareParticipants(): Participant[] {
        return this.participants.filter(
            (participant) => participant?.screenTrack?.readyState === 'live',
        );
    }

    /**
     * Gets the list of moderators, from the [participants]{@link ParticipantService#participants} getter
     *  - Sets the [endSessionText]{@link RoomControlsComponent#endSessionText} getter in the RoomControlsComponent
     */
    get moderators(): Participant[] {
        return this.participants.filter(
            (participant) => participant.userData?.isModerator,
        );
    }

    /**
     * The height of the participants container
     *  - Used to set the [participantsContainerHeight]{@link ParticipantService#participantsContainerHeight} getter
     */
    private _participantsContainerHeight = 0;

    /**
     * Gets the height of the participants container
     *  - Used to set the [participantsContainerHeight]{@link ParticipantService#participantsContainerHeight} getter
     *  - Used in the [setupResizeParticipantEvents]{@link RoomComponent#setupResizeParticipantEvents} in the RoomComponent
     */
    public get participantsContainerHeight() {
        return this._participantsContainerHeight;
    }

    /**
     * Sets the [participantsContainerHeight]{@link ParticipantService#participantsContainerHeight} getter
     */
    public set participantsContainerHeight(value) {
        this._participantsContainerHeight = value;
    }

    /**
     * Gets the maximum number of participants in view
     *  - Dependent on the number of participants in the call, whether the screen is being shared, and the height of the participants container
     *  - Used to set the [participantPaginationCount]{@link ParticipantService#participantPaginationCount} getter
     *  - Used to set the [participantsOffset]{@link ParticipantService#participantsOffset} getter
     *  - Used to set the [participantsInView]{@link ParticipantService#participantsInView} getter
     *  - Used to set the [hasPagination]{@link RoomComponent#hasPagination} getter in the RoomComponent
     */
    get participantInViewMax(): number {
        let maxLength = this.participants.filter(
            (p) => !p.userData?.isObserver,
        ).length;

        if (maxLength < 1) {
            return 1;
        }

        if (window.innerWidth <= 800) {
            return 4;
        }

        if (this.screenShareService.isSharingScreen.value) {
            if (maxLength >= 6 && this.participantsContainerHeight < 600) {
                return 6;
            }

            if (maxLength >= 8 && this.participantsContainerHeight < 800) {
                return 8;
            }

            if (maxLength >= 10 && this.participantsContainerHeight > 800) {
                return 10;
            }
        }

        if (this.participants.length >= 16) {
            maxLength = 16;
        }

        return maxLength;
    }

    /**
     * Sets the pagination count for the participants, based on the number in view
     *  - Used to set the [participantPaginationCount]{@link RoomComponent#participantPaginationCount} getter in the RoomComponent
     */
    get participantPaginationCount() {
        return Array(
            Math.ceil(this.participants.length / this.participantInViewMax),
        )
            .fill(0)
            .map((x, i) => i);
    }

    /**
     * Gets the total number of participants up to the active page.
     *  - Used to set the [participantsInView]{@link ParticipantService#participantsInView} getter
     */
    get participantsOffset() {
        return this.participantInViewMax * this.participantsActivePage;
    }

    /**
     * Gets the list of participants in view
     * - Used to set the [participantsNotInView]{@link ParticipantService#participantsNotInView} getter
     *  - Used in [updateCurrentSpeaker()]{@link ParticipantService#updateCurrentSpeaker} to check if the current speaker is in view
     *  - Used to set [participantsInView]{@link RoomComponent#participantsInView} getter in the RoomComponent
     */
    get participantsInView() {
        return this.participants
            .filter((p) => !p.userData?.isObserver)
            .slice(
                this.participantsOffset,
                this.participantsOffset + this.participantInViewMax,
            );
    }

    /**
     * Gets the list of participants not in view
     *  - Used to set the [participantsNotInView]{@link RoomComponent#participantsNotInView} getter in the RoomComponent
     */
    get participantsNotInView() {
        return this.participants
            .filter((p) => !p.userData?.isObserver)
            .filter((participant: Participant) => {
                return !this.participantsInView.includes(participant);
            });
    }

    /**
     * Gets the user's active mic
     *  - Used in [setupForm]{@link SetupComponent#setupForm} in the SetupComponent, to set the mic in the [devicesForm]{@link SetupComponent#devicesForm}
     */
    public get activeMic() {
        return this.activeDevices?.mic;
    }

    /**
     * Gets the user's active camera
     * - Used in [setupForm]{@link SetupComponent#setupForm} in the SetupComponent, to set the camera in the [devicesForm]{@link SetupComponent#devicesForm}
     */
    public get activeCamera() {
        return this.activeDevices?.camera;
    }

    /**
     * Gets the user's active speaker
     * - Used in [setupForm]{@link SetupComponent#setupForm} in the SetupComponent, to set the speaker in the [devicesForm]{@link SetupComponent#devicesForm}
     */
    public get activeSpeaker() {
        return this.activeDevices?.speaker;
    }

    /**
     * Gets the list of available cameras from the [availableInputDevices]{@link ParticipantService#availableInputDevices}
     *  - Sets the [availableCameras]{@link SetupComponent#availableCameras} getter in the SetupComponent
     */
    public get availableCameras() {
        const availableCameras = this.availableInputDevices?.filter(
            (device: any) => device.kind === 'videoinput',
        );

        return availableCameras;
    }

    /**
     * Gets the list of available mics from the [availableInputDevices]{@link ParticipantService#availableInputDevices}
     *  - Sets the [availableMics]{@link SetupComponent#availableMics} getter in the SetupComponent
     */
    public get availableMics() {
        const availableMics = this.availableInputDevices?.filter(
            (device: any) => device.kind === 'audioinput',
        );

        return availableMics;
    }

    /**
     * Gets the list of available speakers from the [availableInputDevices]{@link ParticipantService#availableInputDevices}]
     *  - Sets the [availableSpeakers]{@link SetupComponent#availableSpeakers} getter in the SetupComponent
     */
    public get availableSpeakers() {
        const availableSpeakers = this.availableInputDevices?.filter(
            (device: any) => device.kind === 'audiooutput',
        );

        return availableSpeakers;
    }

    /**
     * Gets the user's audio level
     *  - Sets the [audioLevel]{@link SetupComponent#audioLevel} getter in the SetupComponent
     */
    get audioLevel() {
        return this.audioLevelUpdated.value * 100;
    }

    /**
     * Gets whether the user's camera is enabled or not
     *  - Sets the [cameraEnabled]{@link RoomControlsComponent#cameraEnabled} getter in the RoomControlsComponent
     *  - Used in [toggleCamera]{@link ParticipantService#toggleCamera} to set the video in the [callObject]{@link ParticipantService#callObject}
     */
    get cameraEnabled() {
        return this.localParticipant?.videoReady;
    }

    /**
     * Gets whether the user's mic is enabled or not
     *  - Sets the [micEnabled]{@link RoomControlsComponent#micEnabled} getter in the RoomControlsComponent
     *  - Used in [togglemic]{@link ParticipantService#togglemic} to set the audio in the [callObject]{@link ParticipantService#callObject}
     */
    get micEnabled() {
        return this.localParticipant?.audioReady;
    }

    /**
     * Gets whether noise cancellation is enabled or not
     *  - Sets the [noiseCancellationEnabled]{@link SetupComponent#noiseCancellationEnabled} getter in the SetupComponent
     */
    get noiseCancellationEnabled() {
        return (
            this.callObject?._inputSettings?.audio?.processor?.type ===
            'noise-cancellation'
        );
    }

    constructor(
        private apiService: ApiService,
        private screenShareService: ScreenShareService,
        private roomService: RoomService,
        private userService: UserService,
    ) {}

    /**
     * Adds a new participant to the call
     *  - Adds the participant to the [participants]{@link ParticipantService#participants} array
     * - If the participant is local, sets the participant's role, dependent on whether they are a moderator, observer, or participant
     * - If the participant is local, Calls [updateParticipant]{@link ApiService#updateParticipant} in the ApiService to update the participant's role in the database
     *  - Calls [setDevices]{@link ParticipantService#setDevices}
     *  - Updates the [participantsUpdated]{@link ParticipantService#participantsUpdated} subject with an empty string
     *  - Called in [setupPreview()]{@link SetupComponent#setupPreview} in the SetupComponent
     * - Called in [handleJoinedMeeting()]{@link StreamProviderService#handleJoinedMeeting} in the StreamProviderService
     * - Called in [participantJoined()]{@link StreamProviderService#participantJoined} in the StreamProviderService
     * @param participant  - The participant to add to the call
     */
    addParticipant(participant: DailyParticipant) {
        if (
            this.roomService.isAiModerated.value &&
            this.participants.some((p) => p.id === participant.session_id)
        ) {
            return;
        }

        this.participants.push(this.formatParticipantObj(participant));

        if (participant.local) {
            if (
                this.roomService.isAiModerated.value &&
                (participant as any).userData?.isPreview
            ) {
                this.checkUserLocation();
            }

            /* @TODO - username isn't bieng passed in here for observers */

            let role;

            if (this.roomService.isTaskBased) {
                role = 'task_taker';
            } else if ((participant as any).userData?.isModerator) {
                role = 'moderator';
            } else if ((participant as any).userData?.isObserver) {
                role = 'observer';
            } else {
                role = 'participant';
            }

            this.apiService
                .updateParticipant({
                    name: participant.user_name,
                    participant_id: participant.session_id,
                    role,
                    spoken_language: this.roomService.spokenLanguage,
                    caption_language: this.roomService.captionLanguage,
                })
                .subscribe(
                    (res) => {},
                    (error: any) => {
                        console.error(error);
                    },
                );
        }

        this.setDevices();
        this.participantsUpdated.next('');
    }

    /**
     * Sets the [activeDevices]{@link ParticipantService#activeDevices} from the input devices in the [callObject]{@link ParticipantService#callObject}
     * Sets the [availableInputDevices]{@link ParticipantService#availableInputDevices} from the enumerated devices in the [callObject]{@link ParticipantService#callObject}
     *  - Updates the [activeDevicesUpdated]{@link ParticipantService#activeDevicesUpdated} subject with an empty string
     * - Updates the [devicesUpdated]{@link ParticipantService#devicesUpdated} subject with an empty string
     *  - Called in [addParticipant()]{@link ParticipantService#addParticipant}
     */
    setDevices() {
        this.getInputDevices();

        this.enumerateDevices();
    }

    getInputDevices() {
        this.callObject
            .getInputDevices()
            .then((device: any) => {
                this.activeDevices = device;
                this.activeDevicesUpdated.next(device);
            })
            .catch((error: any) => {
                console.error(error);
            });
    }

    enumerateDevices() {
        this.callObject
            .enumerateDevices()
            .then((devices: any) => {
                this.availableInputDevices = devices.devices;
                this.devicesUpdated.next('');
            })
            .catch((error: any) => {
                console.error(error);
            });
    }

    /**
     * Changes the media device for the user
     *  - If the type is 'mic' or 'camera', sets the input devices in the [callObject]{@link ParticipantService#callObject} to change the media device, and then calls [updateLocalParticipant]{@link ParticipantService#updateLocalParticipant}
     * - If the type is not 'mic' or 'camera, sets the output device in the [callObject]{@link ParticipantService#callObject} to change the media device, and then calls [updateLocalParticipant]{@link ParticipantService#updateLocalParticipant}
     *  - Called in [changeMediaDevice()]{@link SetupComponent#changeMediaDevice} in the SetupComponent
     * @param type {string} - The type of media device to change.
     * @param deviceId {string} - The device ID to change to
     */
    changeMediaDevice(type: string, deviceId: string | null) {
        if (type === 'mic' || type === 'camera') {
            const source = type === 'mic' ? 'audioDeviceId' : 'videoDeviceId';
            let newInput: any = {};
            newInput[source] = deviceId;

            this.callObject
                .setInputDevicesAsync(newInput)
                .then((data: any) => {
                    // this.setDevices();
                    this.updateLocalParticipant();
                })
                .catch((error: any) => {
                    console.error(error);
                });
        } else {
            this.callObject
                .setOutputDeviceAsync({
                    outputDeviceId: deviceId,
                })
                .then((data: any) => {
                    this.updateLocalParticipant();
                })
                .catch((error: any) => {
                    console.error(error);
                });
        }
    }

    /**
     * Toggles the user's camera on and off
     *  - If the camera is enabled, sets the local video in the [callObject]{@link ParticipantService#callObject} to false
     * - If the camera is disabled, sets the local video in the [callObject]{@link ParticipantService#callObject} to true
     * - Updates the [tracksUpdated]{@link ParticipantService#tracksUpdated} subject with an empty string
     *  - Called in [toggleCamera()]{@link RoomControlsComponent#toggleCamera} in the RoomControlsComponent
     */
    toggleCamera() {
        if (this.cameraEnabled) {
            this.callObject.setLocalVideo(false);
        } else {
            this.callObject.setLocalVideo(true);
        }
        this.tracksUpdated.next('');
    }

    /**
     * Toggles the user's mic on and off
     *  - If the mic is enabled, sets the local audio in the [callObject]{@link ParticipantService#callObject} to false
     * - If the mic is disabled, sets the local audio in the [callObject]{@link ParticipantService#callObject} to true
     * - Updates the [tracksUpdated]{@link ParticipantService#tracksUpdated} subject with an empty string
     *  - Called in [togglemic()]{@link RoomControlsComponent#togglemic} in the RoomControlsComponent
     */
    togglemic() {
        if (this.micEnabled) {
            this.callObject.setLocalAudio(false);
        } else {
            this.callObject.setLocalAudio(true);
        }
        this.tracksUpdated.next('');
    }

    /**
     * Takes the DailyParticipant and formats it into a Participant object
     *  - Used to format the participant object in [addParticipant]{@link ParticipantService#addParticipant}
     * - Used to format the participant object in [updateTrack]{@link ParticipantService#updateTrack}
     * - Used to format the participant object in [updateLocalParticipant]{@link ParticipantService#updateLocalParticipant}
     * @param p {DailyParticipant} - The participant to format
     * @returns {Participant} - The formatted participant object
     */
    formatParticipantObj(p: DailyParticipant): Participant {
        const { video, audio, screenVideo, screenAudio } = p.tracks;

        const videoTrack = video?.persistentTrack;
        const audioTrack = audio?.persistentTrack;
        const screenTrack = screenVideo?.persistentTrack;
        const screenAudioTrack = screenAudio?.persistentTrack;
        return {
            tracks: p.tracks,
            videoTrack: videoTrack,
            audioTrack: audioTrack,
            screenTrack: screenTrack,
            screenAudioTrack: screenAudioTrack,
            videoReady: !!(
                videoTrack &&
                (video.state === PLAYABLE_STATE ||
                    video.state === LOADING_STATE)
            ),
            audioReady: !!(
                audioTrack &&
                (audio.state === PLAYABLE_STATE ||
                    audio.state === LOADING_STATE)
            ),
            screenTrackReady: !!(
                screenTrack &&
                (screenVideo.state === PLAYABLE_STATE ||
                    screenVideo.state === LOADING_STATE)
            ),
            screenAudioReady: !!(
                screenAudioTrack &&
                (screenAudio?.state === PLAYABLE_STATE ||
                    screenAudio?.state === LOADING_STATE)
            ),
            userName: p.user_name,
            local: p.local,
            id: p.session_id,
            permissions: { ...p.permissions, hasPresence: false },
            userData: p.userData,
        };
    }

    /**
     * Updates the video / audio / screen track for a participant, depending on the new track type
     *  - Updates the [tracksUpdated]{@link ParticipantService#tracksUpdated} subject with an empty string
     *  - Called in [handleTrackStartStopped]{@link StreamProviderService#handleTrackStartStopped} in the StreamProviderService
     * @param participant {DailyParticipant} - The participant whose track is to be formatted
     * @param newTrackType {string} - The type of track to update
     * @returns if there is no existing participant, returns
     */
    updateTrack(participant: DailyParticipant, newTrackType: string): void {
        const existingParticipant = this.participants.find(
            (p: Participant) => p.id === participant.session_id,
        );

        if (!existingParticipant) {
            return;
        }

        const currentParticipantCopy = this.formatParticipantObj(participant);

        if (newTrackType === 'video') {
            if (
                existingParticipant.videoReady !==
                currentParticipantCopy.videoReady
            ) {
                existingParticipant.videoReady =
                    currentParticipantCopy.videoReady;
            }

            if (
                currentParticipantCopy.videoReady &&
                existingParticipant.videoTrack?.id !==
                    currentParticipantCopy.videoTrack?.id
            ) {
                existingParticipant.videoTrack =
                    currentParticipantCopy.videoTrack;
            }
        }

        if (newTrackType === 'audio') {
            if (
                existingParticipant.audioReady !==
                currentParticipantCopy.audioReady
            ) {
                existingParticipant.audioReady =
                    currentParticipantCopy.audioReady;
            }

            if (
                currentParticipantCopy.audioReady &&
                existingParticipant.audioTrack?.id !==
                    currentParticipantCopy.audioTrack?.id
            ) {
                existingParticipant.audioTrack =
                    currentParticipantCopy.audioTrack;
            }
        }

        if (newTrackType === 'screenVideo') {
            if (
                existingParticipant.screenTrackReady !==
                currentParticipantCopy.screenTrackReady
            ) {
                existingParticipant.screenTrackReady =
                    currentParticipantCopy.screenTrackReady;
            }

            if (
                currentParticipantCopy.screenTrackReady &&
                existingParticipant.screenTrack?.id !==
                    currentParticipantCopy.screenTrack?.id
            ) {
                existingParticipant.screenTrack =
                    currentParticipantCopy.screenTrack;
            }
        }

        if (newTrackType === 'screenAudio') {
            if (
                existingParticipant.screenAudioReady !==
                currentParticipantCopy.screenAudioReady
            ) {
                existingParticipant.screenAudioReady =
                    currentParticipantCopy.screenAudioReady;
            }

            if (
                currentParticipantCopy.screenAudioReady &&
                existingParticipant.screenAudioTrack?.id !==
                    currentParticipantCopy.screenAudioTrack?.id
            ) {
                existingParticipant.screenAudioTrack =
                    currentParticipantCopy.screenAudioTrack;
            }
        }

        this.tracksUpdated.next('');
    }

    /**
     * Sets grantRequestedAccess to true for the waiting participant with the given id, in the [callObject]{@link ParticipantService#callObject}
     *  - Called in [grantRoomAccess()]{@link ParticipantService#grantRoomAccess} in the ParticipantService
     *  - Called in [admit]{@link ParticipantWaitingComponent#admit} in the ParticipantWaitingComponent
     * @param id {string} - The id of the participant to whom grant access is granted
     */
    grantRoomAccess(id: string) {
        this.callObject.updateWaitingParticipant(id, {
            grantRequestedAccess: true,
        });
    }

    /**
     * Sets grantRequestedAccess to false for the waiting participant with the given id, in the [callObject]{@link ParticipantService#callObject}
     *  - Called in [denyRoomAccess()]{@link ParticipantWaitingComponent#denyRoomAccess} in the ParticipantWaitingComponent
     * @param id {string} - The id of the participant for whom grant access is denied
     */
    denyRoomAccess(id: string) {
        this.callObject.updateWaitingParticipant(id, {
            grantRequestedAccess: false,
        });
    }

    /**
     * Grants access to all participants in the waiting room
     *  - Calls [grantRoomAccess()]{@link ParticipantService#grantRoomAccess} for each participant in the waiting room
     *  - Called in [admitAll()]{@link ParticipantGroupComponent#admitAll} in the ParticipantGroupComponent
     */
    grantAllAccess() {
        this.waitingRoomParticipants?.forEach((participant: any) => {
            this.grantRoomAccess(participant.id);
        });
    }

    /**
     * Leaves the current session using the [callObject]{@link ParticipantService#callObject}
     *  - Called in [endSession] {@link RoomControlsComponent#endSession} in the RoomControlsComponent
     */
    leaveSession() {
        this.callObject.leave();
    }

    /**
     * Updates who the current speaker is
     *  - Updates [currentSpeakerUpdated]{@link ParticipantService#currentSpeakerUpdated} subject with an empty string
     *  - Called in [handleCurrentSpeakerChange] {@link StreamProviderService#handleCurrentSpeakerChange} in the StreamProviderService
     * @param participantId - The id for the participant to update as the current speaker
     * @returns - If the [participantsInView]{@link ParticipantService#participantsInView} includes the current speaker, returns
     * @returns - Sorts the [participants]{@link ParticipantService#participants} array returns for the correct current speaker
     */
    updateCurrentSpeaker(participantId: any) {
        this.participants.forEach((participant: any) => {
            if (participant.id === participantId) {
                participant.currentSpeaker = true;
            } else {
                participant.currentSpeaker = false;
            }
        });

        const currentSpeaker = this.participants.find(
            (participant) => participant.currentSpeaker,
        );

        if (this.participantsInView.includes(currentSpeaker as any)) {
            this.currentSpeakerUpdated.next('');
            return;
        }

        this.participants.sort((a, b) => {
            if (a.currentSpeaker && !b.currentSpeaker) {
                return -1;
            } else if (!a.currentSpeaker && b.currentSpeaker) {
                return 1;
            } else {
                return 0;
            }
        });
        this.currentSpeakerUpdated.next('');
    }

    /**
     * Updates the local participant in the [participants]{@link ParticipantService#participants} array
     *  - Called in [changeMediaDevice]{@link ParticipantService#changeMediaDevice}
     */
    updateLocalParticipant(userName?: string) {
        // this.participants = this.participants.filter((p: any) => p.local);
        const index = this.participants.findIndex((p: any) => p.local);
        // Guard the -1 case: called during device-change/join races before the
        // local participant is in the array; participants[-1].userData throws.
        if (index === -1) {
            return;
        }
        const newUserObject = {
            ...this.callObject?.participants()?.local,
            userData: this.participants[index].userData,
        };

        if (userName) {
            newUserObject.user_name = userName;
        }

        this.participants[index] = this.formatParticipantObj(newUserObject);
    }

    /**
     * Updates the name of a participant in the participants array.
     *  - Finds the participant by their user ID and updates their userName property.
     *  - Called when a participant's name is changed in the application.
     * @param participant - The participant object containing the user_id and the new user_name.
     */
    updateParticipantName(participant: any) {
        const index = this.participants.findIndex(
            (p: any) => p.id === participant.user_id,
        );

        if (index === -1) {
            return;
        }

        this.participants[index].userName = participant.user_name;
        this.participantsUpdated.next('name');
    }

    updateParticipantNetworkQuality(participant: any, status: string) {
        const index = this.participants.findIndex((p: any) => p.local);

        if (index === -1) {
            return;
        }

        // userData can be undefined on a participant; ensure it exists before
        // assigning so this doesn't throw.
        if (!this.participants[index].userData) {
            this.participants[index].userData = {};
        }
        this.participants[index].userData.networkQualityState = status;

        this.participantsUpdated.next('network');
    }

    /**
     *  Filters out the previewing participant from the [participants]{@link ParticipantService#participants}
     *  - Called in [handleNextStep]{@link SetupComponent#handleNextStep} in the SetupComponent
     */
    removePreview() {
        this.participants = this.participants.filter(
            (p: Participant) => !p.userData?.isPreview,
        );
    }

    getLocalParticipantNetworkQuality() {
        return this.participants.find((p: Participant) => p.local)?.userData
            ?.networkQualityState;
    }

    getParticipantNetworkQuality(participantId: string) {
        return this.participants.find(
            (p: Participant) => p.id === participantId,
        )?.userData?.networkQualityState;
    }

    /**
     * Updates the noise cancellation
     *  - Called in [toggleNoiseCancellation]{@link SetupComponent#toggleNoiseCancellation} in the SetupComponent
     * @returns the updated [callObject]{@link ParticipantService#callObject}
     */
    toggleNoiseCancellation() {
        return this.callObject.getInputSettings().then((data: any) => {
            return this.callObject.updateInputSettings({
                audio: {
                    processor: {
                        type:
                            data.audio?.processor?.type === 'noise-cancellation'
                                ? 'none'
                                : 'noise-cancellation',
                    },
                },
            });
        });
    }

    checkUserLocation() {
        if (!this.userService.additionalData) {
            return;
        }

        let additionalData: any = this.apiService.decodeAdditionalData(
            this.userService.additionalData,
        );

        let expectedLocale = additionalData.vpm_target_market;

        if (expectedLocale === 'UK') {
            expectedLocale = 'GB';
        }

        if (expectedLocale === 'AUS') {
            expectedLocale = 'AU';
        }

        if (expectedLocale === 'CA-FR' || expectedLocale === 'CA-EN') {
            expectedLocale = 'CA';
        }

        const userLocale = Intl.DateTimeFormat()
            .resolvedOptions()
            .locale.split('-')[1];

        let userData: any = {
            userLocale,
            expectedLocale,
            status: userLocale === expectedLocale ? 'success' : 'failure',
        };

        LogRocket.track('response-ip-check', userData);
    }
}
